<script>
import ExpenseCodeRow from '@/components/organism/checkout/ExpenseCodeRow'
import Spinner from '@/components/atoms/Spinner'
import ButtonLoader from '@/components/atoms/ButtonLoader'
// import isEmpty from 'lodash.isempty'
import { mapGetters } from 'vuex'
import { ExpenseCodeSearch } from '@/api'

const expenseCodeSearch = new ExpenseCodeSearch()

export default {
    name: 'ExpenseCodeBlock',
    components: { Spinner, ExpenseCodeRow, ButtonLoader },
    provide() {
        return { expenseCodeRemote: this.remote }
    },
    props: {
        canteen: Boolean,
        active: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            model: [],
            errors: [],
            isValidByRow: [],
            loading: false,
            // Provided to the rows: mutated in place, never replaced (injections are not reactive)
            remote: {
                generation: 0,
                lists: null,
                searchFor: (fieldId, search) => this.searchRemoteField(fieldId, search),
            },
        }
    },
    computed: {
        ...mapGetters(['smDevice', 'lgDevice']),

        selectedExpenseRule() {
            return this.canteen
                ? this.$cart.calculation.cloudcanteen
                : this.$cart.calculation.selected_expense_rule
        },
        remainder() {
            return Number(
                (100 - this.model.reduce((p, c) => p + Number(c.distribution), 0)).toFixed(2)
            )
        },
        hasEmptyRow() {
            return (
                this.model.length > 1 &&
                this.model.reduce((isEmptyRow, row) => {
                    if (!isEmptyRow)
                        isEmptyRow = !Object.values(row?.codes || {}).find((value) => value)
                    return isEmptyRow
                }, false)
            )
        },
        isSaved() {
            return (
                !this.isDirty ||
                JSON.stringify(this.model) === JSON.stringify(this.$cart.calculation.expense_code)
            )
        },
        isValid() {
            return !this.isValidByRow.includes(false)
        },

        isDirty() {
            return (
                JSON.stringify(this.model) !== JSON.stringify(this.$cart.model.expense_code) &&
                (this.model.length > 1 ||
                    !!Object.values(this.model?.[0]?.codes || {}).find((value) => value))
            )
        },
        distributionError() {
            return this.remainder !== 0
                ? this.$t('labels.expense_code_allocation_total', {
                      total: 100,
                      by: this.remainder,
                  })
                : null
        },
        expenseCodeFlags() {
            return {
                hasEmptyRow: this.hasEmptyRow,
                isSaved: this.isSaved,
                isValid: this.isValid,
                isDirty: this.isDirty,
                distributionError: this.distributionError,
            }
        },
        // With a selected allowance its editor is authoritative, the canteen editor never claims the flags
        publishesFlags() {
            return this.active && !(this.canteen && this.$cart.hasExpenseRule)
        },
        cart() {
            return this.$cart
        },
        fieldIds() {
            return (this.selectedExpenseRule?.additional_expense_codes || [])
                .map((expenseCode) => String(expenseCode.id))
                .sort()
        },
        fieldSignature() {
            return `${this.canteen ? 'cloudcanteen' : 'allowance'}:${
                this.selectedExpenseRule?.id || ''
            }:${this.fieldIds.join(',')}`
        },
        remoteContextKey() {
            const company = this.$user.model?.company
            if (!this.active || company?.allow_company_level_expense_code !== true) return null
            return this.selectedExpenseRule?.id ? this.fieldSignature : null
        },
    },

    watch: {
        remoteContextKey: {
            immediate: true,
            handler(key) {
                const generation = ++this.remote.generation
                this.remoteContext = key ? key.split(':') : null
                this.remoteCache = new Map()
                this.remote.lists = null
                if (!key) return
                this.fetchRemote('').then(
                    (lists) => {
                        if (generation === this.remote.generation) this.remote.lists = lists
                    },
                    () => {
                        if (generation === this.remote.generation) this.remote.lists = null
                    }
                )
            },
        },
        fieldSignature() {
            if (this.selectedExpenseRule) this.normalizeModel()
        },
        active(active) {
            if (active) this.prepareModel()
            this.syncFlagsOwner()
        },
        publishesFlags() {
            this.syncFlagsOwner()
        },
        expenseCodeFlags(flags) {
            if (this.publishesFlags) this.$cart.publishExpenseCodeFlags(this._uid, flags)
        },
    },
    created() {
        // A hidden editor cannot be edited: its first activation hydrates it again from the cart
        this.hydratedWhileActive = false
    },
    mounted() {
        this.hydratedWhileActive = this.active
        this.initExpenseRuleCodes()
        this.syncFlagsOwner()
    },
    beforeDestroy() {
        this.$cart.releaseExpenseCodeFlags(this._uid)
    },
    methods: {
        fetchRemote(search) {
            const key = (search || '').trim()
            let request = this.remoteCache.get(key)
            if (!request) {
                const [type, id] = this.remoteContext
                const cache = this.remoteCache
                request = expenseCodeSearch.search(type, id, key)
                cache.set(key, request)
                request.catch(() => {
                    if (cache.get(key) === request) cache.delete(key)
                })
            }
            return request
        },
        async searchRemoteField(fieldId, search) {
            const generation = this.remote.generation
            if (!this.remoteContext) return null
            try {
                const lists = await this.fetchRemote(search)
                return generation === this.remote.generation ? lists[fieldId] || null : null
            } catch {
                return null
            }
        },
        calculateDistribution() {
            const distribution = Math.round((100 / this.model.length + Number.EPSILON) * 100) / 100

            let remainder = (10000 - distribution * this.model.length * 100) / 100
            for (let i = 0; i < this.model.length; i++) {
                this.$set(
                    this.model[i],
                    'distribution',
                    i + 1 === this.model.length && remainder !== 0
                        ? (distribution + remainder).toFixed(2)
                        : distribution.toFixed(2)
                )
            }
        },
        syncFlagsOwner() {
            if (this.publishesFlags)
                this.$cart.claimExpenseCodeFlags(this._uid, this.expenseCodeFlags)
            else this.$cart.releaseExpenseCodeFlags(this._uid)
        },
        prepareModel() {
            if (this.hydratedWhileActive) {
                this.normalizeModel()
                return
            }
            this.hydratedWhileActive = true
            this.initExpenseRuleCodes()
        },
        initExpenseRuleCodes() {
            this.model = []
            this.errors = []
            this.isValidByRow = []
            if (!this.$cart.model.expense_code?.length) {
                this.addExpenseRuleCodes()
                return
            }
            this.model = JSON.parse(JSON.stringify(this.$cart.model.expense_code))
            this.errors = this.model.map(() => this.getDefault())
            this.isValidByRow = this.model.map(() => true)
            this.normalizeModel()
        },
        // Every row gets exactly the fields of the selected rule: kept values stay, new fields are null
        normalizeModel() {
            // Without a known rule only the visible card editor (default field only) is reshaped;
            // a hidden or canteen editor keeps its rows until the rule arrives
            if (!this.selectedExpenseRule && (this.canteen || !this.active)) return
            const shape = this.getDefault()
            this.model = this.model.map((row) => {
                const current = row?.codes || {}
                const codes = {}
                Object.keys(current).forEach((key) => {
                    if (Object.prototype.hasOwnProperty.call(shape, key)) codes[key] = current[key]
                })
                Object.keys(shape).forEach((key) => {
                    if (!Object.prototype.hasOwnProperty.call(codes, key)) codes[key] = null
                })
                return { ...row, codes }
            })
            this.errors = this.model.map((_, index) =>
                Object.keys(shape).reduce((errors, key) => {
                    errors[key] = this.errors[index]?.[key] || null
                    return errors
                }, {})
            )
            this.isValidByRow = this.model.map((_, index) => this.isValidByRow[index] !== false)
        },
        addExpenseRuleCodes() {
            const index = this.model?.length || 0
            this.$set(this.model, index, {
                codes: index ? { ...this.model[index - 1].codes } : this.getDefault(),
            })
            this.$set(this.errors, index, index ? { ...this.errors[index - 1] } : this.getDefault())
            this.$set(this.isValidByRow, index, index ? this.isValidByRow[index - 1] : true)
            this.calculateDistribution()
        },
        getDefault() {
            return (
                this.selectedExpenseRule?.additional_expense_codes?.reduce(
                    (p, c) => {
                        p[c.id] = null
                        return p
                    },
                    { default: null }
                ) || { default: null }
            )
        },
        deleteRow(index) {
            this.model.splice(index, 1)
            this.errors.splice(index, 1)
            this.isValidByRow.splice(index, 1)
            this.calculateDistribution()
        },
        validate() {
            this.model.forEach((_, index) => {
                this.$refs[`ExpenseCodeRow-${index}`]?.[0]?.validateRow()
            })
        },
        async save() {
            try {
                this.normalizeModel()
                await this.$nextTick()
                this.validate()
                if (!this.isValid) return
                this.loading = true
                this.$cart.model.expense_code = JSON.parse(JSON.stringify(this.model))
                await this.$cart.calculate()
            } finally {
                this.loading = false
            }
        },
        clear() {
            if (this.isSaved && this.$cart.model.expense_code) {
                this.$cart.model.expense_code = null
                this.$cart.calculate()
            }
            this.model = []
            this.errors = []
            this.isValidByRow = []
            this.addExpenseRuleCodes()
        },
    },
}
</script>

<template lang="pug">
.expense-code-block(id="expense_code")
    .grid.gap-8.mb-8
        expense-code-row(
            v-for="(_, index) in model" 
            v-model="model[index]"
            :is-valid.sync="isValidByRow[index]"
            :error.sync="errors[index]"
            :canteen="canteen"
            :ref="`ExpenseCodeRow-${index}`"
            :key="`ExpenseCodeRow-${index}`" 
            :labels="!index" :multiple="model.length > 1" 
            @delete="deleteRow(index)")
    .alert.alert-light-danger.mb-4.font-weight-semibold.text-center(v-if="remainder !== 0") 
        icon.mr-2(icon="fa-solid fa-exclamation-triangle")
        | {{ $t('labels.expense_code_allocation_total', {total: 100, by: remainder}) }}
    .alert.alert-light-info.mb-4.font-weight-semibold.text-center(v-if="!isSaved") 
        icon.mr-2(icon="fa-solid fa-exclamation-triangle")
        | {{$t('order.checkout_errors.expense_code_not_saved') }}
    button.btn.btn-link.font-weight-semibold(@click="addExpenseRuleCodes") 
        u {{ $t('base.add_new') }}
    .d-flex.justify-content-end.align-items-center.gap-4
        button.btn.btn-secondary(@click="clear") {{ $t('base.cancel') }}
        button-loader.btn.btn-primary(@click="save" :disabled="loading || remainder !== 0 || !isDirty || isSaved" :loading="loading" inline) {{ $t('base.save') }}     
</template>
